import React from 'react';
import { Link } from '@inertiajs/react';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { AEO_DATA, quoted as Q } from '~/marketing/marketingData';
import { Post, Section, P, Sources, Cta } from '~/marketing/Post';
import { word } from '~/marketing/research/figures';

/* The choices any AI visibility measurement makes, and the ones we made.
   Approved 2026-10-13 from `docs/blog/drafts.md`, post 3. **Most of it is
   Class 2, read from `quoted`**, because what a figure counts is a claim about
   accuracy; the joining prose is Class 1.

   **It closes on the trial, not the free check**: the free check asks one
   question once, and a post arguing that one answer on its own can mislead should not
   send its reader to one. */
export default function MeasuringAiVisibility() {
  return (
    <Post path="/blog/how-to-measure-ai-visibility" lede={<>
      There is more than one way to measure whether AI assistants recommend a business, and each answers a different question. Here are the choices any measurement makes, what each one misses, and the ones we made.
    </>}>
      <Section title="One answer, or many">
        <P>The simplest check is to ask once and look.</P>
        <P>{Q.variability}</P>
        <P>
          One answer shows what one person might have seen once. A count across repeated answers shows how often you are named.
        </P>
      </Section>

      <Section title="Anywhere, or in your city">
        <P>
          Without a city in the question, the answer may not be about your market at all, and a local business competes in its own city. The same question in two cities names different businesses: in pest control, the chains that led in Dallas were outside the top four in Boise.
        </P>
        <P>{Q.google_method}</P>
      </Section>

      <Section title="One score, or each AI assistant">
        <P>
          Folding every AI assistant into a single number hides the difference between them. One can name you in most answers while another never does, and the fix for each can differ. Look at each AI assistant, then at the total.
        </P>
      </Section>

      <Section title="A count, or a count out of something">
        <P>
          “Named three times” means little until you know out of how many. Some answers recommend nobody, and some searches show no AI answer at all.
        </P>
        <P>{Q.denominator_basis}</P>
        <P>{Q.google_none}</P>
      </Section>

      <Section title="Named, or cited">
        <P>
          Being named and being used as a source are different things, and a measurement should say which it counts.
        </P>
        <P>{Q.citation_measurement}</P>
      </Section>

      <Section title="What the AI assistant returns, or what a person sees">
        <P>{Q.measurement_basis}</P>
      </Section>

      <Section title="How often">
        <P>{Q.weekly_not_daily}</P>
      </Section>

      <Section title="What we chose">
        <P>
          Every figure a Spruce plan reports is a count across repeated answers, asked in your city, shown for each AI assistant as well as in total, with its denominator and the date it was measured. The whole method is on our <Link href={routes.methodology}>methodology page</Link>.
        </P>
      </Section>

      <Sources lead="The pest control example comes from" />
      <Cta heading={`Track your business this way, free for ${word(AEO_DATA.trial.days)} days.`} label="Start free" href={routes.signUp} />
    </Post>
  );
}

MeasuringAiVisibility.layout = withMarketingLayout;
